/**
 * One payment spread over a party's open bills, oldest first (pay from the
 * statement; M2: bills that pay on themselves take a payment line each). The
 * server writes the lines this decides; the form shows the same spread
 * before it is sent. Pure, exact: figures are worked at nine places.
 */
import type { PayBill } from '../api';
import { decimalOf, exactOf } from './matches';

export type PayShare = { readonly bill: PayBill; readonly amount: string };

/** What the bills owe in all, an exact decimal. */
export const billsDue = (bills: readonly PayBill[]): string => decimalOf(bills.reduce((sum, bill) => sum + exactOf(bill.due), 0n));

/**
 * The share of each bill, oldest first, until the amount is spent. Null when
 * the amount is not above nought or is more than the bills owe in all: a
 * payment line never pays a bill past what it owes.
 */
export const spreadPayment = (bills: readonly PayBill[], amount: string): readonly PayShare[] | null => {
  let left = exactOf(amount);
  if (left <= 0n || left > exactOf(billsDue(bills))) return null;
  const ordered = [...bills].sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0));
  const out: PayShare[] = [];
  for (const bill of ordered) {
    if (left === 0n) break;
    const due = exactOf(bill.due);
    if (due <= 0n) continue;
    const take = due < left ? due : left;
    out.push({ bill, amount: decimalOf(take) });
    left -= take;
  }
  return out;
};
